import * as React from 'react';
import { styled } from '@linaria/react';
import { Play, Pause } from 'lucide-react';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useHasHydrated from '@/hooks/use-has-hydrated';

import InlineCode from '@/components/InlineCode';
import Space from '@/components/Space';
import Spinner from '@/components/Spinner';
import FakeBrowserWindow from '@/components/FakeBrowserWindow';
import ButtonPlayful from '@/components/ButtonPlayful';

function MainUseCaseDemo({}) {
  const [playState, setPlayState] = React.useState('paused');

  const hasHydrated = useHasHydrated();
  const isLoading = !hasHydrated;

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    setPlayState(prefersReducedMotion ? 'paused' : 'running');
  }, [prefersReducedMotion]);

  return (
    <OuterWrapper>
      <Wrapper>
        <Header>
          <Backdrop />
          <BackdropEdge />
          <Logo>Some Website</Logo>
        </Header>
        <Content>
          <Cupcake
            src="/images/backdrop-filter/cupcake.png"
            style={{ animationPlayState: playState }}
          />
          <p>
            This is an example website showing how I typically use{' '}
            <InlineCode>backdrop-filter</InlineCode> to create glassy
            headers.
          </p>
          <OptionalP>
            Notice that as the cupcake moves behind the header, it
            appears blurry, as it would if it was passing behind
            frosted glass.
          </OptionalP>
          <Actions>
            <Button
              Icon={
                isLoading
                  ? Spinner
                  : playState === 'running'
                    ? Pause
                    : Play
              }
              backgroundColor="hsl(175deg 60% 25%)"
              highlightColor="hsl(168deg 70% 35%)"
              boopEffect={{
                scale: 1.1,
              }}
              onClick={() =>
                setPlayState(
                  playState === 'running' ? 'paused' : 'running'
                )
              }
            >
              Play/Pause Animation
            </Button>
          </Actions>
        </Content>
      </Wrapper>
    </OuterWrapper>
  );
}

const OuterWrapper = styled(FakeBrowserWindow)`
  margin-top: 2rem;
  margin-bottom: 4rem;
  margin-inline: -1rem;
  isolation: isolate;
`;

const Wrapper = styled(Space)`
  border-radius: 0;
  /* Lots of bottom padding to introduce a scrollbar */
  padding-bottom: 1rem;
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  min-height: 4rem;
  padding: 1rem;
  background: linear-gradient(
    to bottom,
    hsl(210deg 15% 6% / 1) 0%,
    hsl(210deg 15% 6% / 0) 100%
  );
`;
const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  height: 8rem;
  backdrop-filter: blur(8px) brightness(90%) saturate(140%);
  pointer-events: none;
  mask-image: linear-gradient(
    to bottom,
    black 0 4rem,
    transparent 4rem
  );
`;
const BackdropEdge = styled.div`
  --thickness: 1px;
  position: absolute;
  inset: 0;
  height: 100%;
  background: hsl(0deg 0% 100% / 0.2);
  backdrop-filter: blur(6px) brightness(110%) saturate(120%);
  pointer-events: none;
  transform: translateY(calc(50% - var(--thickness)));
  opacity: 0.5;
  height: 8rem;
  mask-image: linear-gradient(
    to bottom,
    black 0 var(--thickness),
    transparent var(--thickness)
  );
`;
const Logo = styled.p`
  position: relative;
  font-weight: var(--font-weight-bold);
  font-size: 1rem;
  text-transform: uppercase;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  padding: 2rem;
  padding-left: 1rem;
  padding-bottom: 0;

  p {
    font-size: 1rem;

    &:not(:last-child) {
      margin-bottom: 1rem;
    }
  }
`;

const OptionalP = styled.p`
  @media (max-width: 45rem) {
    display: none;
  }
`;

const Cupcake = styled.img`
  float: right;
  width: 200px;
  margin-left: 32px;
  margin-top: -64px;
  animation: bounce 4000ms alternate infinite ease-in-out;

  @media (max-width: 37.5rem) {
    float: revert;
    margin-inline: auto;
    margin-bottom: 2rem;
  }

  @keyframes bounce {
    0% {
      transform: rotate(10deg) translateY(-20px);
    }

    100% {
      transform: rotate(10deg) translateY(20px);
    }
  }
`;

const Actions = styled.div`
  margin-top: 2rem;
`;

const Button = styled(ButtonPlayful)`
  outline: 1px solid var(--color-gray-200);
`;
export default MainUseCaseDemo;
